/**
 * 游戏风格对话界面
 * 类似视觉小说/RPG的对话系统
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Animated,
  Image,
  Modal,
  Alert,
} from 'react-native';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { incrementGuestMessageCount } from '../store/authSlice';
import backendAI from '../services/ai';
import conversationService from '../services/conversationService';
import { supabase } from '../config/supabase';
import { API_BASE_URL } from '../config/api';
import { VERSION_INFO } from '../config/version';

// 尝试导入PNG图片，如果不存在则使用fallback
let backgroundImage: any = null;
let princessImage: any = null;
let dogImage: any = null;

try {
  backgroundImage = require('../assets/images/background.png');
} catch (error) {
  console.log('Background PNG not found, using fallback');
}

try {
  princessImage = require('../assets/images/princess.png');
} catch (error) {
  console.log('Princess PNG not found, using fallback');
}

try {
  dogImage = require('../assets/images/dog.png');
} catch (error) {
  console.log('Dog PNG not found, using fallback');
}

const { width, height } = Dimensions.get('window');

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface GameStyleChatProps {
  onNavigateToPractice: () => void;
  onNavigateToProfile: () => void;
  onMessagesChange?: (messages: Message[]) => void;
  initialMessages?: Message[]; // 支持从外部传入初始消息
}

// 获取服务器域名（截断显示）
const getServerDomain = () => {
  try {
    const url = new URL(API_BASE_URL);
    const hostname = url.hostname;
    // 限制显示长度为15个字符，超出部分用省略号代替
    if (hostname.length > 15) {
      return hostname.substring(0, 12) + '...';
    }
    return hostname;
  } catch {
    const fallback = API_BASE_URL;
    if (fallback.length > 15) {
      return fallback.substring(0, 12) + '...';
    }
    return fallback;
  }
};

const GameStyleChat: React.FC<GameStyleChatProps> = ({
  onNavigateToPractice,
  onNavigateToProfile,
  onMessagesChange,
  initialMessages = [], // 默认为空数组
}) => {
  const dispatch = useAppDispatch();
  const { user, isGuestMode, guestUsage } = useAppSelector((state) => state.auth);

  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState<Message | null>(null);
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string>('dog'); // 默认使用dog头像
  const [userAvatarType, setUserAvatarType] = useState<'emoji' | 'image'>('image'); // 默认使用图片类型
  const [isTyping, setIsTyping] = useState(false);
  const [practiceReady, setPracticeReady] = useState(false);
  const [showPracticeHint, setShowPracticeHint] = useState(false);
  const [practiceButtonScale] = useState(new Animated.Value(1));
  const [isLoading, setIsLoading] = useState(false);

  // 动画值
  const teacherOpacity = useRef(new Animated.Value(1)).current;
  const userOpacity = useRef(new Animated.Value(0.3)).current;
  const textOpacity = useRef(new Animated.Value(0)).current;
  const textScale = useRef(new Animated.Value(0.9)).current;

  useEffect(() => {
    initializeConversation();
  }, []);

  // 初始化对话：创建或加载现有对话
  const initializeConversation = async () => {
    try {
      setIsLoading(true);
      console.log('🚀 GameStyleChat - 初始化对话...');

      // 首先设置认证token
      try {
        // 使用与backendAI相同的token获取逻辑
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          conversationService.setAuthToken(session.access_token);
          console.log('✅ ConversationService token设置成功');
        } else if (user?.id) {
          conversationService.setAuthToken(user.id);
          console.log('✅ ConversationService使用用户ID作为token');
        }
      } catch (tokenError) {
        console.warn('⚠️ Token设置失败，使用默认认证:', tokenError);
      }

      // 创建新对话
      const conversation = await conversationService.createConversation({
        title: '游戏对话',
        interface_style: 'game',
      });

      setConversationId(conversation.id);
      console.log('✅ 对话创建成功:', conversation.id);

      // 添加欢迎消息到数据库
      const welcomeMessage = await conversationService.addMessage({
        conversation_id: conversation.id,
        content: '你好！我是公主殿下！很高兴见到你！今天想聊些什么呢？',
        is_user: false,
        message_type: 'chat',
      });

      // 转换为本地格式
      const localWelcomeMessage: Message = {
        id: welcomeMessage.id,
        text: welcomeMessage.content,
        isUser: welcomeMessage.is_user,
        timestamp: new Date(welcomeMessage.created_at),
      };

      setMessages([localWelcomeMessage]);
      setCurrentMessage(localWelcomeMessage);
      setRecentMessages([localWelcomeMessage]);
      onMessagesChange?.([localWelcomeMessage]);
      animateMessageAppear();

    } catch (error) {
      console.error('❌ 初始化对话失败:', error);
      // 降级到离线模式，但仍然生成对话ID用于后续操作
      const offlineConversationId = `offline_${Date.now()}`;
      setConversationId(offlineConversationId);
      console.log('🔄 使用离线对话ID:', offlineConversationId);
      
      const welcomeMessage: Message = {
        id: Date.now().toString(),
        text: '你好！我是公主殿下！很高兴见到你！今天想聊些什么呢？',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
      setCurrentMessage(welcomeMessage);
      setRecentMessages([welcomeMessage]);
      onMessagesChange?.([welcomeMessage]);
      animateMessageAppear();
    } finally {
      setIsLoading(false);
    }
  };

  const animateMessageAppear = () => {
    // 重置动画值
    textOpacity.setValue(0);
    textScale.setValue(0.9);

    // 文字出现动画
    Animated.parallel([
      Animated.timing(textOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(textScale, {
        toValue: 1,
        tension: 150,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const switchSpeaker = (isUserSpeaking: boolean) => {
    Animated.parallel([
      Animated.timing(teacherOpacity, {
        toValue: isUserSpeaking ? 0.3 : 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(userOpacity, {
        toValue: isUserSpeaking ? 1 : 0.3,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const addMessage = async (text: string, isUser: boolean) => {
    console.log('🚀 GameStyleChat - 即将添加消息到数据库:', {
      text: text.substring(0, 30),
      isUser,
      conversationId,
    });

    if (!conversationId) {
      console.error('❌ 没有对话ID，无法保存消息');
      return;
    }

    let finalMessage: Message;

    try {
      // 只有当conversationId不是离线模式时才尝试保存到数据库
      if (!conversationId.startsWith('offline_')) {
        // 保存到数据库，添加超时处理
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Database save timeout')), 10000)
        );
        
        const savePromise = conversationService.addMessage({
          conversation_id: conversationId,
          content: text,
          is_user: isUser,
          message_type: 'chat',
        });

        const savedMessage = await Promise.race([savePromise, timeoutPromise]) as any;

        // 转换为本地格式
        finalMessage = {
          id: savedMessage.id,
          text: savedMessage.content,
          isUser: savedMessage.is_user,
          timestamp: new Date(savedMessage.created_at),
        };

        console.log('✅ GameStyleChat - 消息已保存到数据库:', finalMessage.id);
      } else {
        throw new Error('Offline mode - skip database save');
      }

    } catch (error) {
      console.error('❌ 保存消息到数据库失败:', error);
      // 降级到本地存储
      finalMessage = {
        id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text,
        isUser,
        timestamp: new Date(),
      };
    }

    // 使用函数式状态更新
    setMessages(prevMessages => {
      const newMessages = [...prevMessages, finalMessage];

      console.log('✅ GameStyleChat - 本地状态已更新:', {
        prevLength: prevMessages.length,
        newLength: newMessages.length,
        userCount: newMessages.filter(m => m.isUser).length,
      });

      // 更新最近的两条消息
      const recent = newMessages.slice(-2);
      setRecentMessages(recent);

      // 通知父组件
      setTimeout(() => onMessagesChange?.(newMessages), 0);

      return newMessages;
    });

    setCurrentMessage(finalMessage);

    // 切换说话者高亮
    switchSpeaker(isUser);

    // 消息出现动画
    setTimeout(() => {
      animateMessageAppear();
    }, 100);

    // 添加消息到AI服务
    backendAI.addMessage({
      id: finalMessage.id,
      text: finalMessage.text,
      isUser: finalMessage.isUser,
      timestamp: finalMessage.timestamp,
    });

    // 检查练习准备度
    setTimeout(() => {
      checkPracticeReadiness();
    }, 100);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) {return;}

    // 检查游客限制
    if (isGuestMode && guestUsage.messageCount >= 5) {
      Alert.alert('使用限制', '游客模式已达到消息限制，请登录继续使用');
      return;
    }

    const userInput = inputText;

    // 立即清空输入框
    setInputText('');
    setIsTyping(true);

    // 添加用户消息
    await addMessage(userInput, true);

    if (isGuestMode) {
      dispatch(incrementGuestMessageCount());
    }

    // 获取AI回复，添加超时处理
    try {
      console.log('🤖 开始生成AI回复...');
      
      // 创建超时Promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('AI reply timeout')), 20000)
      );
      
      const replyPromise = backendAI.generateSmartReply();
      const reply = await Promise.race([replyPromise, timeoutPromise]) as string;

      console.log('✅ AI回复获取成功:', reply);
      setTimeout(async () => {
        console.log('📤 添加AI回复到消息列表');
        await addMessage(reply, false);
        setIsTyping(false);
      }, 1000 + Math.random() * 1000); // 1-2秒延迟，模拟思考
    } catch (error) {
      console.error('❌ AI回复失败:', error);
      const fallbackReplies = [
        '听起来很有趣！能再详细说说吗？',
        '你的想法很有意思呢！',
        '哇，真的吗？然后呢？',
        '我很好奇你的经历！继续讲讲吧～',
      ];
      const randomReply = fallbackReplies[Math.floor(Math.random() * fallbackReplies.length)];

      console.log('🔄 使用fallback回复:', randomReply);
      setTimeout(async () => {
        console.log('📤 添加fallback回复到消息列表');
        await addMessage(randomReply, false);
        setIsTyping(false);
      }, 1500);
    }
  };

  // 练习按钮动画
  const startPracticeButtonAnimation = () => {
    // 脉搏动画效果
    const pulseAnimation = () => {
      Animated.sequence([
        Animated.timing(practiceButtonScale, {
          toValue: 1.1,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(practiceButtonScale, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
      ]).start(() => {
        // 如果还在练习准备状态，继续动画
        if (practiceReady) {
          setTimeout(pulseAnimation, 1000);
        }
      });
    };

    pulseAnimation();
  };

  // 检查练习准备情况 - 使用当前状态中的消息数量
  const checkPracticeReadiness = () => {
    // 如果已经准备好练习，跳过检查防止无限循环
    if (practiceReady) {
      console.log('⏭️ 练习已准备好，跳过检查');
      return;
    }
    
    // 使用当前的messages状态
    const actualUserMessages = messages.filter(msg => msg.isUser);

    console.log('检查练习准备度 - 总消息数:', messages.length);
    console.log('检查练习准备度 - 用户消息数:', actualUserMessages.length);
    console.log('检查练习准备度 - 当前practiceReady状态:', practiceReady);
    console.log('检查练习准备度 - 用户消息详情:', actualUserMessages.map(m => ({ text: m.text.substring(0, 20), isUser: m.isUser })));

    // 检查是否需要触发练习准备
    if (messages.length >= 5 && !practiceReady) {
      console.log('🎆 触发练习准备！总消息数达到', messages.length, '条（用户消息数:', actualUserMessages.length, '）');
      setPracticeReady(true);
      setShowPracticeHint(true);

      // 练习按钮动画提示
      startPracticeButtonAnimation();

      // 只显示UI提示，不添加消息到历史记录以避免无限循环
      console.log('✅ 练习准备完成，显示练习提示气泡（不添加消息）');
      // 8秒后自动隐藏提示
      setTimeout(() => {
        setShowPracticeHint(false);
      }, 8000);
    }
  };


  return (
    <View style={styles.container}>
      {/* 背景 */}
      <View style={styles.background}>
        {/* 使用PNG背景图片（如果可用） */}
        {backgroundImage && (
          <Image
            source={backgroundImage}
            style={styles.backgroundImage}
            resizeMode="cover"
          />
        )}
      </View>

      {/* 公主角色 - 左上角 */}
      <Animated.View style={[styles.princessCharacter, { opacity: teacherOpacity }]}>
        <View style={styles.princessAvatar}>
          {princessImage ? (
            <Image
              source={princessImage}
              style={styles.princessImage}
              resizeMode="contain"
            />
          ) : (
            <View style={styles.animeHead}>
              <View style={styles.animeHair} />
              <View style={styles.animeFace}>
                <View style={styles.animeEyes}>
                  <View style={styles.animeEye} />
                  <View style={styles.animeEye} />
                </View>
                <View style={styles.animeMouth} />
              </View>
            </View>
          )}
        </View>
        <Text style={styles.princessName}>公主殿下</Text>
      </Animated.View>

      {/* 用户角色 - 右下角 */}
      <Animated.View style={[styles.userCharacter, { opacity: userOpacity }]}>
        <TouchableOpacity
          style={styles.userAvatarContainer}
          onPress={() => setShowAvatarSelector(true)}
        >
          {userAvatarType === 'image' && userAvatar === 'dog' && dogImage ? (
            <Image
              source={dogImage}
              style={styles.userImage}
              resizeMode="contain"
            />
          ) : (
            <Text style={styles.userAvatarEmoji}>{userAvatar}</Text>
          )}
        </TouchableOpacity>
        <Text style={styles.userName}>你</Text>
      </Animated.View>

      {/* 漫画风格对话气泡 - 显示最近两条消息 */}
      {recentMessages.map((message, index) => {
        const isLatest = index === recentMessages.length - 1;
        const opacity = isLatest ? textOpacity : 0.7; // 最新消息完全不透明，之前的消息稍微透明

        return (
          <Animated.View
            key={message.id}
            style={[
              message.isUser ? styles.userSpeechBubble : styles.princessSpeechBubble,
              {
                opacity: opacity,
                transform: [{ scale: isLatest ? textScale : 0.95 }],
                // 如枟有两条消息，调整位置实现向上叠加效果
                ...(recentMessages.length === 2 && !message.isUser && index === 0 ? {
                  top: 160, // 公主的旧消息往上移
                } : {}),
                ...(recentMessages.length === 2 && message.isUser && index === 0 ? {
                  bottom: 320, // 用户的旧消息向上叠加，与公主消息保持一致的向上效果
                } : {}),
              },
            ]}
          >
            <Text style={message.isUser ? styles.userBubbleText : styles.princessBubbleText}>
              {message.text}
            </Text>
            {isTyping && !message.isUser && isLatest && (
              <Text style={styles.typingIndicator}>正在输入...</Text>
            )}
          </Animated.View>
        );
      })}

      {/* 练习提示气泡 */}
      {showPracticeHint && (
        <Animated.View style={styles.practiceHintBubble}>
          <Text style={styles.practiceHintText}>
            🎆 点击“🚀 练习”开始挑战！
          </Text>
        </Animated.View>
      )}

      {/* 输入区域 - 底部 */}
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.textInput}
          value={inputText}
          onChangeText={setInputText}
          placeholder="说些什么... (Enter发送)"
          multiline={false}
          maxLength={200}
          editable={!isTyping}
          onSubmitEditing={() => {
            // Enter键发送消息
            if (inputText.trim() && !isTyping) {
              handleSendMessage();
            }
          }}
          blurOnSubmit={false}
        />
        <TouchableOpacity
          style={[styles.sendButton, (!inputText.trim() || isTyping) && styles.sendButtonDisabled]}
          onPress={handleSendMessage}
          disabled={!inputText.trim() || isTyping}
        >
          <Text style={styles.sendButtonText}>💬</Text>
        </TouchableOpacity>
      </View>

      {/* 顶部标题栏 */}
      <View style={styles.headerContainer}>
        <Text style={styles.appTitle}>ChatFluent v{VERSION_INFO.version}</Text>
        <View style={styles.serverStatus}>
          <View style={styles.statusIndicator} />
          <Text style={styles.statusText}>{getServerDomain()}</Text>
        </View>
      </View>

      {/* 功能按钮 */}
      <View style={styles.actionButtons}>
        <Animated.View
          style={{
            transform: [{ scale: practiceReady ? practiceButtonScale : 1 }],
          }}
        >
          <TouchableOpacity
            style={[styles.actionButton, practiceReady && styles.actionButtonHighlight]}
            onPress={() => {
              console.log('🔍 练习按钮被点击 - 开始诊断');
              console.log('练习按钮被点击 - 当前消息数:', messages.length);
              console.log('练习按钮被点击 - 用户消息数:', messages.filter(m => m.isUser).length);

              // 详细诊断每条消息
              console.log('🔍 练习按钮被点击 - 所有消息详情:');
              messages.forEach((msg, index) => {
                console.log(`  ${index + 1}. "${msg.text.substring(0, 20)}..." (isUser: ${msg.isUser}, 类型: ${typeof msg.isUser}, ID: ${msg.id})`);
              });

              // 特别检查：寻找可能的用户消息
              console.log('🔍 专门寻找用户相关消息:');
              messages.forEach((msg, index) => {
                // 检查那些可能是用户输入的消息
                if (msg.text && !msg.text.includes('公主殿下') && !msg.text.includes('你好！我是') &&
                    !msg.text.includes('很高兴见到你') && !msg.text.includes('练习') &&
                    !msg.text.includes('听起来') && !msg.text.includes('然后呢')) {
                  console.log(`  🔎 可疑用户消息 ${index + 1}: "${msg.text}" (isUser: ${msg.isUser}, 应该是用户消息)`);
                }
              });

              // 使用JSON序列化检查
              console.log('🔍 消息数组JSON:', JSON.stringify(messages.map(m => ({ text: m.text.substring(0, 15), isUser: m.isUser })), null, 2));

              // 确保最新消息状态被传递，强制同步
              onMessagesChange?.(messages);

              // 添加延迟确保状态同步
              setTimeout(() => {
                // 停止动画
                setPracticeReady(false);
                onNavigateToPractice();
              }, 100);
            }}
          >
            <Text style={[styles.actionButtonText, practiceReady && styles.actionButtonTextHighlight]}>
              {practiceReady ? '🎆 练习准备好！' : '🚀 练习'}
            </Text>
          </TouchableOpacity>
        </Animated.View>
        <TouchableOpacity style={styles.actionButton} onPress={onNavigateToProfile}>
          <Text style={styles.actionButtonText}>👤 个人</Text>
        </TouchableOpacity>
      </View>

      {/* 头像选择器 */}
      <Modal visible={showAvatarSelector} transparent animationType="slide">
        <View style={styles.modalContainer}>
          <View style={styles.avatarSelector}>
            <Text style={styles.avatarSelectorTitle}>选择你的头像</Text>
            <View style={styles.avatarGrid}>
              {/* 表情头像 */}
              {['👤', '😊', '🧑‍💼', '👩‍💼', '🧑‍🎓', '👩‍🎓', '😎', '🤓'].map((avatar, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.avatarOption,
                    userAvatar === avatar && userAvatarType === 'emoji' && styles.selectedAvatar,
                  ]}
                  onPress={() => {
                    setUserAvatar(avatar);
                    setUserAvatarType('emoji');
                    setShowAvatarSelector(false);
                  }}
                >
                  <Text style={styles.avatarOptionText}>{avatar}</Text>
                </TouchableOpacity>
              ))}

              {/* PNG狗狗头像选项（如果可用） */}
              {dogImage && (
                <TouchableOpacity
                  style={[
                    styles.avatarOption,
                    styles.imageAvatarOption,
                    userAvatar === 'dog' && userAvatarType === 'image' && styles.selectedAvatar,
                  ]}
                  onPress={() => {
                    setUserAvatar('dog');
                    setUserAvatarType('image');
                    setShowAvatarSelector(false);
                  }}
                >
                  <Image
                    source={dogImage}
                    style={styles.avatarOptionImage}
                    resizeMode="contain"
                  />
                </TouchableOpacity>
              )}
            </View>
            <TouchableOpacity
              style={styles.closeAvatarSelector}
              onPress={() => setShowAvatarSelector(false)}
            >
              <Text style={styles.closeAvatarSelectorText}>关闭</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#f0f8ff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backgroundImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  userImage: {
    width: (height / 3) * (2 / 3),  // 屏幕高度的三分之一再乘以三分之二
    height: (height / 3) * (2 / 3),
    // 去掉圆角，保持原图比例
  },
  imageAvatarOption: {
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarOptionImage: {
    width: 35,
    height: 35,
    borderRadius: 18,
  },
  // 公主角色样式 - 左边
  princessCharacter: {
    position: 'absolute',
    top: 305,  // 调整到305，配合新的标题栏布局
    left: 0,
    alignItems: 'flex-start',
  },
  princessAvatar: {
    // 去掉背景和边框，直接显示图片
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  princessImage: {
    width: height / 3,  // 屏幕高度的三分之一
    height: height / 3,
    // 去掉圆角，保持原图比例
  },
  princessName: {
    marginTop: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#FF69B4',
    textAlign: 'left',
    textShadowColor: 'rgba(255,255,255,0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
    alignSelf: 'flex-start',
  },
  // 用户角色样式 - 右下角
  userCharacter: {
    position: 'absolute',
    bottom: 80,
    right: 10,
    alignItems: 'center',
  },
  userAvatarContainer: {
    // 去掉背景和边框，直接显示图片
    justifyContent: 'center',
    alignItems: 'center',
  },
  userAvatarEmoji: {
    fontSize: 35,
  },
  userName: {
    marginTop: 6,
    fontSize: 14,
    fontWeight: '600',
    color: '#4CAF50',
    textAlign: 'center',
    textShadowColor: 'rgba(255,255,255,0.8)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
  },
  animeHead: {
    width: 60,
    height: 65,
    alignItems: 'center',
  },
  animeHair: {
    width: 50,
    height: 30,
    backgroundColor: '#8B4513',
    borderRadius: 25,
    marginBottom: -8,
  },
  animeFace: {
    width: 40,
    height: 35,
    backgroundColor: '#FDBCB4',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  animeEyes: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  animeEye: {
    width: 8,
    height: 6,
    backgroundColor: '#333',
    borderRadius: 4,
    marginHorizontal: 3,
  },
  animeMouth: {
    width: 6,
    height: 3,
    backgroundColor: '#ff6b9d',
    borderRadius: 3,
  },
  // 公主对话气泡 - 左边
  princessSpeechBubble: {
    position: 'absolute',
    top: 220,
    left: 20,
    maxWidth: width * 0.7,
    backgroundColor: '#FFE4E1',
    borderRadius: 20,
    borderBottomLeftRadius: 5,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 2,
    borderColor: '#FF69B4',
  },
  princessBubbleText: {
    fontSize: 15,
    lineHeight: 20,
    color: '#8B0000',
  },
  // 用户对话气泡 - 右边
  userSpeechBubble: {
    position: 'absolute',
    bottom: 220,
    right: 20,
    maxWidth: width * 0.65,
    backgroundColor: '#E8F5E8',
    borderRadius: 20,
    borderBottomRightRadius: 5,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 2,
    borderColor: '#4CAF50',
  },
  userBubbleText: {
    fontSize: 15,
    lineHeight: 20,
    color: '#2E7D32',
  },
  typingIndicator: {
    fontSize: 11,
    color: '#666',
    fontStyle: 'italic',
    marginTop: 5,
  },
  // 练习提示气泡
  practiceHintBubble: {
    position: 'absolute',
    top: 70,
    left: 20,
    right: 20,
    backgroundColor: '#FFF3CD',
    borderRadius: 15,
    borderWidth: 2,
    borderColor: '#FFE066',
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
  },
  practiceHintText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#856404',
    textAlign: 'center',
  },
  inputContainer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    flexDirection: 'row',
    alignItems: 'flex-end',
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 25,
    paddingHorizontal: 15,
    paddingVertical: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  textInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginRight: 10,
    maxHeight: 80,
    fontSize: 16,
    color: '#333',
  },
  sendButton: {
    width: 40,
    height: 40,
    backgroundColor: '#4CAF50',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#ccc',
  },
  sendButtonText: {
    fontSize: 20,
  },
  // 顶部标题栏样式
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    paddingHorizontal: 20,
    paddingTop: 18,  // 压缩一半，从35改为18
    paddingBottom: 8,  // 压缩一半，从15改为8
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  appTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  serverStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
    marginRight: 6,
  },
  statusText: {
    fontSize: 12,
    color: '#666',
  },
  actionButtons: {
    position: 'absolute',
    top: 50,  // 进一步减少，配合压缩的标题栏
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.98)',
    paddingHorizontal: 20,
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  actionButton: {
    backgroundColor: 'transparent',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 18,
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4CAF50',
  },
  actionButtonHighlight: {
    backgroundColor: '#E8F5E8',
    transform: [{ scale: 1.05 }],
  },
  actionButtonTextHighlight: {
    color: '#2E7D32',
    fontSize: 13,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarSelector: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 20,
    width: width * 0.8,
  },
  avatarSelectorTitle: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 20,
    color: '#333',
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
  },
  avatarOption: {
    width: 60,
    height: 60,
    backgroundColor: '#f0f0f0',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedAvatar: {
    borderColor: '#4CAF50',
    backgroundColor: '#e8f5e8',
  },
  avatarOptionText: {
    fontSize: 30,
  },
  closeAvatarSelector: {
    backgroundColor: '#4CAF50',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 10,
  },
  closeAvatarSelectorText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default GameStyleChat;
